import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useGallery, recordView } from '@/hooks/useGallery';
import { photoThumbnail } from '@/lib/api';
import { EVENT_NAME, EVENT_DATE, EVENT_SUBTITLE } from '@/lib/supabase';
import { Modal } from '@/components/ui/Modal';
import { HomePageBackground } from '@/components/ui/PageBackground';
import LikeButton from '@/components/gallery/LikeButton';
import CommentList from '@/components/gallery/CommentList';
import type { Photo } from '@/types';

const ADMIN_TOKEN_KEY = 'guestlens_admin_token';

export default function HomePage() {
  const { data: photos = [] } = useGallery();
  const isAdminHint = !!localStorage.getItem(ADMIN_TOKEN_KEY);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);

  const handleOpenPhoto = (p: Photo) => {
    setSelectedPhoto(p);
    recordView(p.id);
  };

  // Ultimele 20 poze pentru preview
  const previewPhotos = photos.slice(0, 20);

  // Mini stats calculate din toate pozele
  const totalPhotos = photos.length;
  const totalLikes = photos.reduce((s, p) => s + (p.likes_count ?? 0), 0);
  const totalComments = photos.reduce((s, p) => s + (p.comments_count ?? 0), 0);
  const topLiked = photos.reduce<Photo | null>(
    (best, p) => (!best || p.likes_count > best.likes_count ? p : best),
    null
  );
  const topCommented = photos.reduce<Photo | null>(
    (best, p) => (!best || p.comments_count > best.comments_count ? p : best),
    null
  );
  // Top uploader
  const uploaderCounts = photos.reduce<Record<string, number>>((acc, p) => {
    acc[p.uploader_name] = (acc[p.uploader_name] ?? 0) + 1;
    return acc;
  }, {});
  const topUploader = Object.entries(uploaderCounts).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="min-h-screen bg-transparent relative">
      <HomePageBackground />
      <header className="relative z-10 border-b border-border/50 bg-bg/80 backdrop-blur-sm">
        <div className="container-page flex items-center justify-between py-md">
          <a
            href="https://guestlens.pages.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="font-serif text-lg hover:text-accent transition-colors"
          >
            GuestLens
          </a>
          <nav className="flex items-center gap-md text-sm">
            <Link to="/upload" className="text-fg-soft hover:text-fg">Încarcă</Link>
            {isAdminHint && (
              <Link to="/admin" className="text-fg-soft hover:text-fg">Admin</Link>
            )}
          </nav>
        </div>
      </header>

      <main className="relative z-10 container-page py-xl">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="eyebrow text-xs uppercase tracking-widest text-fg-muted">
            {EVENT_DATE}
          </p>
          <h1 className="mt-md font-serif text-4xl sm:text-5xl">{EVENT_NAME}</h1>
          <p className="mt-md text-fg-soft">{EVENT_SUBTITLE}</p>
          <div className="mt-lg flex justify-center gap-md">
            <Link to="/upload" className="btn-primary">Încarcă o poză</Link>
            <Link to="/gallery" className="btn-secondary">Vezi galeria</Link>
          </div>
        </motion.section>

        {previewPhotos.length > 0 && (
          <section className="mt-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl">Ultimele momente</h2>
              <Link to="/gallery" className="text-sm text-fg-soft hover:text-fg">
                Deschide albumul complet →
              </Link>
            </div>
            <div className="mt-md grid grid-cols-2 gap-sm sm:grid-cols-3 sm:gap-md md:gap-lg">
              {previewPhotos.map((p) => {
                const url = photoThumbnail(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleOpenPhoto(p)}
                    className="block aspect-[4/3] overflow-hidden rounded-md border border-border bg-bg p-1.5 transition-transform hover:scale-[1.02] cursor-pointer text-left w-full"
                  >
                    {url && (
                      <img
                        src={url}
                        alt={p.caption ?? `Poză de ${p.uploader_name}`}
                        className="h-full w-full object-cover rounded-sm"
                        loading="lazy"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Mini Stats */}
        {totalPhotos > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-2xl mx-auto max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-md">
              <span className="flex-1 h-px bg-border" />
              <p className="font-mono text-[10px] uppercase tracking-widest text-fg/40">Statistici</p>
              <span className="flex-1 h-px bg-border" />
            </div>

            {/* Counters row */}
            <div className="grid grid-cols-3 gap-sm text-center">
              {[
                { icon: '📷', value: totalPhotos, label: 'poze' },
                { icon: '❤️', value: totalLikes, label: 'aprecieri' },
                { icon: '💬', value: totalComments, label: 'comentarii' },
              ].map(({ icon, value, label }) => (
                <div
                  key={label}
                  className="rounded-lg border border-border bg-bg-alt p-sm flex flex-col items-center gap-1"
                  style={{ boxShadow: 'var(--shadow-sm)' }}
                >
                  <span className="text-xl">{icon}</span>
                  <p className="font-serif text-2xl leading-none">{value}</p>
                  <p className="font-mono text-[10px] text-fg/50">{label}</p>
                </div>
              ))}
            </div>

            {/* Highlights */}
            <div className="mt-sm grid grid-cols-1 sm:grid-cols-3 gap-sm">
              {topUploader && (
                <div className="rounded-lg border border-border bg-bg-alt p-sm flex items-center gap-sm" style={{ boxShadow: 'var(--shadow-sm)' }}>
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-bg-alt font-serif text-sm">
                    {topUploader[0].charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-mono text-[9px] uppercase tracking-wider text-fg/40">👑 Cel mai activ</p>
                    <p className="text-sm font-medium truncate">{topUploader[0]}</p>
                    <p className="font-mono text-[10px] text-fg/50">{topUploader[1]} poze</p>
                  </div>
                </div>
              )}
              {topLiked && topLiked.likes_count > 0 && (
                <button
                  type="button"
                  onClick={() => handleOpenPhoto(topLiked)}
                  className="rounded-lg border border-border bg-bg-alt p-sm flex items-center gap-sm hover:border-accent/40 transition-colors cursor-pointer text-left w-full"
                  style={{ boxShadow: 'var(--shadow-sm)' }}
                >
                  {photoThumbnail(topLiked) && (
                    <img
                      src={photoThumbnail(topLiked)}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-md object-cover"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="font-mono text-[9px] uppercase tracking-wider text-fg/40">❤️ Cele mai multe like-uri</p>
                    <p className="text-sm font-medium truncate">{topLiked.uploader_name}</p>
                    <p className="font-mono text-[10px] text-fg/50">{topLiked.likes_count} aprecieri</p>
                  </div>
                </button>
              )}
              {topCommented && topCommented.comments_count > 0 && (
                <button
                  type="button"
                  onClick={() => handleOpenPhoto(topCommented)}
                  className="rounded-lg border border-border bg-bg-alt p-sm flex items-center gap-sm hover:border-accent/40 transition-colors cursor-pointer text-left w-full"
                  style={{ boxShadow: 'var(--shadow-sm)' }}
                >
                  {photoThumbnail(topCommented) && (
                    <img
                      src={photoThumbnail(topCommented)}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-md object-cover"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="font-mono text-[9px] uppercase tracking-wider text-fg/40">💬 Cele mai multe comentarii</p>
                    <p className="text-sm font-medium truncate">{topCommented.uploader_name}</p>
                    <p className="font-mono text-[10px] text-fg/50">{topCommented.comments_count} comentarii</p>
                  </div>
                </button>
              )}
            </div>
          </motion.section>
        )}
      </main>

      {/* Popup modal pentru poze */}
      <Modal open={!!selectedPhoto} onClose={() => setSelectedPhoto(null)}>
        {selectedPhoto && (
          <div className="max-h-[85vh] overflow-y-auto">
            <img
              src={photoThumbnail(selectedPhoto)}
              alt={selectedPhoto.caption || ''}
              className="max-h-[55vh] w-full rounded-md object-contain bg-bg"
            />
            <div className="mt-md flex items-center justify-between">
              <div>
                <p className="font-serif text-lg leading-tight">{selectedPhoto.uploader_name}</p>
                {selectedPhoto.caption && (
                  <p className="mt-1 text-sm text-fg-soft">{selectedPhoto.caption}</p>
                )}
                <p className="font-mono text-xs text-fg/70 mt-1">
                  {new Date(selectedPhoto.uploaded_at).toLocaleDateString('ro-RO')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-fg-soft">
                  👁 {selectedPhoto.views_count ?? 0}
                </span>
                <LikeButton photoId={selectedPhoto.id} initialCount={selectedPhoto.likes_count} />
              </div>
            </div>

            {/* Comment Section */}
            <CommentList photoId={selectedPhoto.id} />
          </div>
        )}
      </Modal>
    </div>
  );
}